import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import type { PortCampaignDraft } from './nudge-library/port-campaign-create-dialog.component';
import type { SiteSuggestionsHandoff } from './nudge-library/site-nudge-suggestions.service';

/** Tabs that can be reached from inside another tab's content. */
export type NudgeTabKey = 'campaigns' | 'detailed' | 'library';
export type DetailedEnvironment = 'production' | 'development';

/**
 * Lets one Nudge-page tab hand over to another.
 *
 * The tabs are unrelated components rendered through `ngComponentOutlet`, so
 * they have no reference to each other or to the tab group. Rather than have
 * each one reach for `NudgeComponent`, they ask here by key and the page
 * resolves the key to an index — which matters because the tab list is filtered
 * by role and privilege, so indices are not fixed.
 *
 * It also carries the campaign the Detailed tab is scoped to, set when a card
 * is opened on the Campaigns tab.
 */
@Injectable({ providedIn: 'root' })
export class NudgePageService {
  readonly campaignsChanged = new Subject<void>();
  readonly campaignListCache = new Map<string, any[]>();
  /**
   * The Detailed tab's last Revenue Uplift responses, by request. Tabs are
   * rebuilt on every visit, so without these coming back to Detailed would
   * show the loader again for charts it just drew.
   */
  readonly revenueChartCache = new Map<string, any>();
  readonly campaignChartCache = new Map<string, any>();
  private readonly tabRequests = new Subject<NudgeTabKey>();
  readonly tabRequests$ = this.tabRequests.asObservable();

  /** `null` = no campaign picked yet; the Detailed tab shows everything. */
  private readonly campaign = new BehaviorSubject<string | null>(null);
  readonly activeCampaignId$ = this.campaign.asObservable();

  get activeCampaignId(): string | null {
    return this.campaign.value;
  }

  openTab(key: NudgeTabKey): void {
    this.tabRequests.next(key);
  }

  selectCampaign(id: string | null): void {
    this.campaign.next(id);
  }

  /**
   * Which side of the Detailed tab's Production | In Development switch to
   * open on. Kept until the Detailed tab takes it, since the tab may not
   * exist yet when the request is made (e.g. "Create Experiments").
   */
  private pendingEnvironment: DetailedEnvironment | null = null;
  private readonly environmentRequests = new Subject<DetailedEnvironment>();
  readonly environmentRequests$ = this.environmentRequests.asObservable();

  showDetailedEnvironment(environment: DetailedEnvironment): void {
    this.pendingEnvironment = environment;
    this.environmentRequests.next(environment);
  }

  /**
   * "New campaign" from the Campaigns tab: the campaign is made in the Nudge
   * Library drawer, next to the nudges it will hold. `draft` pre-fills the
   * form when there is one. `suggestions` are the site's suggested nudges,
   * which the library shows on top and pre-selects. Kept until the library
   * takes it, since the library tab may not exist yet when it is asked for.
   */
  private pendingCampaign: {
    draft: PortCampaignDraft | null;
    suggestions?: SiteSuggestionsHandoff;
  } | null = null;
  private readonly campaignRequests = new Subject<void>();
  readonly campaignRequests$ = this.campaignRequests.asObservable();

  startCampaignInLibrary(
    draft: PortCampaignDraft | null = null,
    suggestions?: SiteSuggestionsHandoff,
  ): void {
    this.pendingCampaign = { draft, suggestions };
    this.campaignRequests.next();
    this.openTab('library');
  }

  takeCampaignRequest(): {
    draft: PortCampaignDraft | null;
    suggestions?: SiteSuggestionsHandoff;
  } | null {
    const request = this.pendingCampaign;
    this.pendingCampaign = null;
    return request;
  }

  takeDetailedEnvironment(): DetailedEnvironment | null {
    const environment = this.pendingEnvironment;
    this.pendingEnvironment = null;
    return environment;
  }
}
